import { PauseIcon, PlayIcon } from '@/components/Icons';
import StyleFilterDetailCard from '@/components/StyleFilterDetailCard/StyleFilterDetailCard';
import { Button } from '@/components/ui/button';
import { useBuyStyleFilter } from '@/pages/StyleFilterMarket/hooks/useBuyStyleFilter';
import { FilterGroup } from '@/service/type/Filter';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { cn, titleCaseToKebabCase } from '@/utils';
import { useNavigate } from 'react-router-dom';

interface FilterCardProps {
  data: FilterGroup;
  className?: string;
}

export default function FilterCard({ data, className }: FilterCardProps) {
  const { play, playAudioInfo, isPlaying, pause, currentTime } = useAudioPlayerStore();

  const { selectedFilter, handleSwitch, handleBuy } = useBuyStyleFilter(data);

  const handlePlay = (filter: FilterGroup) => {
    if (isPlaying && playAudioInfo?.id === String(filter.id)) {
      pause();
    } else {
      play({
        url: filter.audio,
        name: filter.name,
        id: String(filter.id),
      });
    }
  };

  const navigate = useNavigate();
  const navigateWithScroll = (path: string) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className={cn('w-full border border-[#202223]/10 rounded-lg p-4 flex-shrink-0', className)}>
      <StyleFilterDetailCard
        onRemove={() => undefined}
        onUse={() => undefined}
        title={data?.artist}
        content={data?.name}
        onSwitch={handleSwitch}
        defaultValue="LOW"
        sfId={data?.id}
        background={data?.album_cover}
        showButtons={false}
        disabled={true}
        switchDisabled={true}
        fontSize={9}
        isMarket={true}
        innerClassName="p-[4px]"
        coverStyle={{
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          height: '273px',
          width: '168px',
          marginBottom: '25px',
          position: 'relative',
          zIndex: 1,
          boxShadow: `
            50.667px 80px 26.667px 0px rgba(0, 0, 0, 0.00), 
            32px 52px 24px 0px rgba(0, 0, 0, 0.03), 
            18.667px 29.333px 20px 0px rgba(0, 0, 0, 0.09), 
            8px 13.333px 14.667px 0px rgba(0, 0, 0, 0.16), 
            2.667px 2.667px 8px 0px rgba(0, 0, 0, 0.19)
          `,
        }}
      />
      {/* Track details */}
      <div className="">
        <div>
          <h2
            className="text-[1rem] text-neutral-500 cursor-pointer hover:text-jen-orange transition-colors tracking-[-3%]"
            onClick={() => {
              navigate(`/marketplace/artist/${titleCaseToKebabCase(data?.artist)}`);
            }}
          >
            Imogen Heap
          </h2>
          <h1
            className="text-[1.25rem] leading-[1.25rem] mt-2 cursor-pointer hover:text-jen-orange transition-colors"
            onClick={() => {
              navigate(`/marketplace/filter/${titleCaseToKebabCase(data?.name)}`);
            }}
          >
            {data?.name}
          </h1>
        </div>

        <div className="flex items-center text-neutral-500 mt-[18px]">
          <span className="text-neutral-500 text-[0.75rem] tracking-[-0.23px]">
            From $4.99 for 90 Min of Low Jeneration or $7.99 for 90 Min of High Jeneration
          </span>
        </div>

        <div className="flex items-center justify-between mt-[12px]">
          <Button
            variant="ghost"
            className="bg-jen-orange hover:bg-jen-orange/80 text-white px-4"
            onClick={() => {
              navigateWithScroll(`/marketplace/filter/${data?.name}`);
            }}
          >
            Buy Now
          </Button>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              rounded
              className="h-10 w-10 bg-jen-orange-10 text-jen-orange relative z-10 active:bg-jen-orange active:text-white"
              onClick={() => handlePlay(data)}
            >
              {isPlaying && playAudioInfo?.id === String(data.id) ? (
                <PauseIcon fillColor="currentColor" />
              ) : (
                <PlayIcon fillColor="currentColor" />
              )}
            </Button>
            {isPlaying && playAudioInfo?.id === String(data.id) && (
              <svg className="absolute top-0 left-0 w-10 h-10 -rotate-90" viewBox="0 0 40 40">
                <circle
                  cx="20"
                  cy="20"
                  r="18"
                  fill="none"
                  stroke="#FF5B27"
                  strokeWidth="2"
                  strokeDasharray={`${(currentTime / data.audio_duration) * (2 * 3.14 * 20)} ${2 * 3.14 * 20}`}
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
